import React, { Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import LoadingPage from '../components/LoadingPage';
import { useAdmin } from '../contexts/admin';
import { useAuth } from '../contexts/auth';
import { FriendRequestsProvider } from '../contexts/friendRequests';

const AccessDeniedPage = React.lazy(() => import('../pages/AccessDenied'));
const Amigos = React.lazy(() => import('../pages/Amigos'));
const EncontrarPessoas = React.lazy(() => import('../pages/EncontrarPessoas'));
const Atividades = React.lazy(() => import('../pages/Atividades'));
const AvaliacaoJogador = React.lazy(() => import('../pages/AvaliacaoJogador'));
const Convite = React.lazy(() => import('../pages/Convite'));
const AceitarConvite = React.lazy(() => import('../pages/AceitarConvite'));
const Grupo = React.lazy(() => import('../pages/Grupo'));
const GruposList = React.lazy(() => import('../pages/GruposList'));
const Home = React.lazy(() => import('../pages/Home'));
const Landing = React.lazy(() => import('../pages/Landing'));
const Login = React.lazy(() => import('../pages/Login'));
const AgendasList = React.lazy(() => import('../pages/AgendasList'));
const Perfil = React.lazy(() => import('../pages/Perfil'));
const Register = React.lazy(() => import('../pages/Register'));
const RegrasEsporte = React.lazy(() => import('../pages/RegrasEsporte'));
const SorteioTimes = React.lazy(() => import('../pages/SorteioTimes'));
const SuperAdminUsersPage = React.lazy(() => import('../pages/SuperAdmin'));
const TermosDeUso = React.lazy(() => import('../pages/TermosDeUso'));
const RateiosList = React.lazy(() => import('../pages/RateiosList'));
const Rateio = React.lazy(() => import('../pages/Rateio'));
const DetalheRateio = React.lazy(() => import('../pages/DetalheRateio'));
const JogadoresGrupo = React.lazy(() => import('../pages/JogadoresGrupo'));
const FeedbackManagement = React.lazy(() => import('../pages/SuperAdmin/FeedbackManagement'));
const Partidas = React.lazy(() => import('../pages/Partidas'));
const GrupoDetalhes = React.lazy(() => import('../pages/GrupoDetalhes'));
const AgendaDetalhes = React.lazy(() => import('../pages/AgendaDetalhes'));
const AvaliacaoResultados = React.lazy(() => import('../pages/AvaliacaoResultados'));
const QuadrasList = React.lazy(() => import('../pages/QuadrasList'));
const QuadraDetail = React.lazy(() => import('../pages/QuadraDetail'));
const CadastroQuadra = React.lazy(() => import('../pages/CadastroQuadra'));
const EditarQuadra = React.lazy(() => import('../pages/EditarQuadra'));
const MinhasQuadras = React.lazy(() => import('../pages/MinhasQuadras'));
const GerenciarFotosQuadra = React.lazy(() => import('../pages/GerenciarFotosQuadra'));
const GerenciarHorarios = React.lazy(() => import('../pages/GerenciarHorarios'));
const MinhasReservas = React.lazy(() => import('../pages/MinhasReservas'));
const ReservaStatus = React.lazy(() => import('../pages/ReservaStatus'));
const Sobre = React.lazy(() => import('../pages/Sobre'));
const RelatorioPresencaAgenda = React.lazy(() => import('../pages/RelatorioPresencaAgenda'));
const NotFound = React.lazy(() => import('../pages/NotFound'));

function PrivateRoute({ children }) {
  const { signed } = useAuth();
  const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';
  
  if (signed || skipAuth) {
    return (
      <FriendRequestsProvider>
        <Navbar />
        {children}
      </FriendRequestsProvider>
    );
  }
  
  return <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { signed, loading: authLoading } = useAuth();
  const { isSuperAdmin, loading: adminLoading } = useAdmin();
  
  if (authLoading || adminLoading) {
    return (
      <>
        <Navbar />
        <LoadingPage message="Verificando permissões..." />
      </>
    );
  }
  
  if (!signed) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isSuperAdmin) {
    return (
      <>
        <Navbar />
        <AccessDeniedPage />
      </>
    );
  }
  
  return (
    <FriendRequestsProvider>
      <Navbar />
      {children}
    </FriendRequestsProvider>
  );
}

function ConviteRoute({ children }) {
  const { signed, loading } = useAuth();
  const location = useLocation();
  const { id } = useParams();

  if (loading) {
    return <LoadingPage message="Carregando..." />;
  }

  if (!signed) {
    const redirectData = {
      path: location.pathname,
      id: id,
      type: 'CONVITE_AMIZADE',
      timestamp: new Date().getTime()
    };
    localStorage.setItem('redirectData', JSON.stringify(redirectData));
    return <Navigate to="/login" replace />;
  }

  return (
    <FriendRequestsProvider>
      <Navbar />
      {children}
    </FriendRequestsProvider>
  );
}

function RoutesApp() {
  const { signed } = useAuth();

  return (
    <Suspense fallback={<LoadingPage />}>
      <Routes>
        <Route path="/" element={!signed ? <Landing /> : <Navigate to="/home" replace />} />
        <Route path="/login" element={signed ? <Navigate to="/home" replace /> : <Login />} />
        <Route path="/register" element={<Register />} />

        {/* Rotas privadas */}
        <Route path="/home" element={<PrivateRoute><Home /></PrivateRoute>} />
        <Route path="/perfil" element={<PrivateRoute><Perfil /></PrivateRoute>} />
        <Route path="/grupos" element={<PrivateRoute><GruposList /></PrivateRoute>} />
        <Route path="/atividades" element={<PrivateRoute><Atividades /></PrivateRoute>} />
        <Route path="/grupo/novo" element={<PrivateRoute><Grupo /></PrivateRoute>} />
        <Route path="/grupo/:id/editar" element={<PrivateRoute><Grupo /></PrivateRoute>} />
        <Route path="/grupo/:id/detalhes" element={<PrivateRoute><GrupoDetalhes /></PrivateRoute>} />
        <Route path="/grupo/:grupoId/agendas" element={<PrivateRoute><AgendasList /></PrivateRoute>} />
        <Route path="/grupo/:grupoId/agenda/:agendaId/detalhes" element={<PrivateRoute><AgendaDetalhes /></PrivateRoute>} />
        <Route path="/grupo/:grupoId/agenda/:agendaId/avaliacoes" element={<PrivateRoute><AvaliacaoResultados /></PrivateRoute>} />
        <Route path="/grupo/:grupoId/agenda/:agendaId/relatorio-presenca" element={<PrivateRoute><RelatorioPresencaAgenda /></PrivateRoute>} />
        <Route path="/amigos" element={<PrivateRoute><Amigos /></PrivateRoute>} />
        <Route path="/encontrar-pessoas" element={<PrivateRoute><EncontrarPessoas /></PrivateRoute>} />
        <Route path="/convite/:id" element={<ConviteRoute><Convite /></ConviteRoute>} />
        <Route path="/grupo/:id/agenda/:idAgenda/sorteio" element={<PrivateRoute><SorteioTimes /></PrivateRoute>} />
        <Route path="/grupo/:id/agenda/:idAgenda/times" element={<PrivateRoute><SorteioTimes visualizarApenas={true} /></PrivateRoute>} />
        <Route path="/grupo/:id/agenda/:idAgenda/partidas" element={<PrivateRoute><Partidas /></PrivateRoute>} />
        <Route path="/grupo/:id/jogadores" element={<PrivateRoute><JogadoresGrupo /></PrivateRoute>} />

        {/* Rotas de convites */}
        <Route path="/grupo/:id/convite" element={<AceitarConvite tipo="grupo" />} />
        <Route path="/rateio/:id/convite" element={<AceitarConvite tipo="rateio" />} />

        <Route path="/regras-sorteio" element={<PrivateRoute><RegrasEsporte /></PrivateRoute>} />
        <Route path="/termos-de-uso" element={<TermosDeUso />} />
        <Route path="/sobre" element={<PrivateRoute><Sobre /></PrivateRoute>} />
        <Route path="/avaliar/:userId" element={<PrivateRoute><AvaliacaoJogador /></PrivateRoute>} />

        {/* Rotas para Rateios */}
        <Route path="/rateios" element={<PrivateRoute><RateiosList /></PrivateRoute>} />
        <Route path="/rateio/novo" element={<PrivateRoute><Rateio /></PrivateRoute>} />
        <Route path="/rateio/:id/editar" element={<PrivateRoute><Rateio /></PrivateRoute>} />
        <Route path="/rateio/:id" element={<PrivateRoute><DetalheRateio /></PrivateRoute>} />

        {/* Rotas para Quadras */}
        <Route path="/quadras" element={<PrivateRoute><QuadrasList /></PrivateRoute>} />
        <Route path="/quadras/cadastrar" element={<PrivateRoute><CadastroQuadra /></PrivateRoute>} />
        <Route path="/quadras/minhas" element={<PrivateRoute><MinhasQuadras /></PrivateRoute>} />
        <Route path="/quadras/:id" element={<PrivateRoute><QuadraDetail /></PrivateRoute>} />
        <Route path="/quadras/:id/editar" element={<PrivateRoute><EditarQuadra /></PrivateRoute>} />
        <Route path="/quadras/:id/gerenciar-fotos" element={<PrivateRoute><GerenciarFotosQuadra /></PrivateRoute>} />
        <Route path="/quadras/:id/gerenciar-horarios" element={<PrivateRoute><GerenciarHorarios /></PrivateRoute>} />

        {/* Rotas para Reservas */}
        <Route path="/reservas" element={<PrivateRoute><MinhasReservas /></PrivateRoute>} />
        <Route path="/reservas/:id" element={<PrivateRoute><ReservaStatus /></PrivateRoute>} />

        {/* Rotas de administração */}
        <Route path="/admin/usuarios" element={<AdminRoute><SuperAdminUsersPage /></AdminRoute>} />
        <Route path="/admin/feedbacks" element={<AdminRoute><FeedbackManagement /></AdminRoute>} />

        {/* Rota de acesso negado */}
        <Route path="/acesso-negado" element={<AccessDeniedPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default RoutesApp;
